import React, { useState, useRef } from 'react';
import { Toast } from 'primereact/toast';
import 'primereact/resources/themes/lara-light-indigo/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import { User } from '../types/user';

interface TopBarProps {
  user?: User | null;  // Made optional with ? to match MainLayout
}
const TopBar: React.FC<TopBarProps> = ({ user }) => {
  const [settingsVisible, setSettingsVisible] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const toast = useRef<Toast>(null);

  const getInitials = (role: string) => {
    if (!role) return 'U';
    return role.substring(0, 2).toUpperCase();
  };

  return (
    <>
      <Toast ref={toast} />

      <div
        style={{
          height: '75px',
          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafb 100%)',
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderBottom: '1px solid #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 40px',
          gap: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          position: 'relative',
          zIndex: 100
        }}
      >
        {/* Left side - Search Bar */}
        <div style={{ 
          flex: '1', 
          maxWidth: '500px',
          position: 'relative'
        }}>
          <div style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
          }}>
            <i className="pi pi-search" style={{
              position: 'absolute',
              left: '16px',
              fontSize: '16px',
              color: '#9ca3af',
              pointerEvents: 'none'
            }}></i>
            <input
              type="text"
              placeholder="Search students, teachers, exams..."
              style={{
                width: '100%',
                padding: '12px 16px 12px 45px',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                fontSize: '14px',
                background: '#f9fafb',
                color: '#1f2937',
                outline: 'none',
                transition: 'all 0.2s ease',
              }}
              onFocus={(e) => {
                e.currentTarget.style.background = '#ffffff';
                e.currentTarget.style.borderColor = '#d4f17e';
                e.currentTarget.style.boxShadow = '0 0 0 3px rgba(212, 241, 126, 0.1)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.background = '#f9fafb';
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.boxShadow = 'none';
              }}
            />
          </div>
        </div>

        {/* Right side - Actions */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Settings Button */}
          <button
            onClick={() => setSettingsVisible(true)}
            style={{
              width: '44px',
              height: '44px',
              background: '#f9fafb',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f3f4f6';
              e.currentTarget.style.borderColor = '#d1d5db';
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = '#f9fafb';
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            <i className="pi pi-cog" style={{ fontSize: '18px', color: '#6b7280' }}></i>
          </button>

          {/* Notification Button */}
          <div style={{ position: 'relative' }}>
            <button
              onClick={() => setNotificationsOpen(!notificationsOpen)}
              style={{
                width: '44px',
                height: '44px',
                background: '#f9fafb',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                position: 'relative',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f3f4f6';
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#f9fafb';
                e.currentTarget.style.borderColor = '#e5e7eb';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <i className="pi pi-bell" style={{ fontSize: '18px', color: '#6b7280' }}></i>
              <span
                style={{
                  position: 'absolute',
                  top: '8px',
                  right: '8px',
                  width: '10px',
                  height: '10px',
                  background: '#ef4444',
                  borderRadius: '50%',
                  border: '2px solid white',
                  animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
                }}
              ></span>
            </button>

            {/* Notifications Dropdown */}
            {notificationsOpen && (
              <div style={{
                position: 'absolute',
                top: '54px',
                right: '0',
                width: '320px',
                background: 'white',
                borderRadius: '12px',
                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15)',
                border: '1px solid #e5e7eb',
                overflow: 'hidden',
                animation: 'slideDown 0.2s ease'
              }}>
                <div style={{
                  padding: '16px 20px',
                  borderBottom: '1px solid #e5e7eb',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                    Notifications
                  </h3>
                  <span style={{
                    background: '#ef4444',
                    color: 'white',
                    fontSize: '11px',
                    fontWeight: '600',
                    padding: '2px 8px',
                    borderRadius: '12px'
                  }}>
                    3 New
                  </span>
                </div>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                  {[
                    { icon: 'pi-user', text: 'New student registration', time: '5 min ago', color: '#3b82f6' },
                    { icon: 'pi-calendar', text: 'Exam scheduled for tomorrow', time: '1 hour ago', color: '#f59e0b' },
                    { icon: 'pi-bell', text: 'Fee payment reminder', time: '2 hours ago', color: '#10b981' }
                  ].map((notif, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: '16px 20px',
                        borderBottom: idx < 2 ? '1px solid #f3f4f6' : 'none',
                        cursor: 'pointer',
                        transition: 'background 0.2s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f9fafb';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start' }}>
                        <div style={{
                          width: '36px',
                          height: '36px',
                          borderRadius: '10px',
                          background: `${notif.color}15`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0
                        }}>
                          <i className={`pi ${notif.icon}`} style={{ fontSize: '16px', color: notif.color }}></i>
                        </div>
                        <div style={{ flex: 1 }}>
                          <p style={{ margin: '0 0 4px 0', fontSize: '14px', color: '#1f2937', fontWeight: '500' }}>
                            {notif.text}
                          </p>
                          <span style={{ fontSize: '12px', color: '#9ca3af' }}>{notif.time}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{
                  padding: '12px 20px',
                  borderTop: '1px solid #e5e7eb',
                  textAlign: 'center'
                }}>
                  <button style={{
                    background: 'transparent',
                    border: 'none',
                    color: '#0f3d3e',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    padding: '4px'
                  }}>
                    View all notifications
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div style={{
            width: '1px',
            height: '40px',
            background: '#e5e7eb'
          }}></div>

          {/* User Profile */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              cursor: 'pointer',
              padding: '8px 12px',
              borderRadius: '12px',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f9fafb';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            <div
              style={{
                width: '44px',
                height: '44px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #0f3d3e 0%, #1a5555 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '16px',
                fontWeight: '700',
                boxShadow: '0 2px 8px rgba(15, 61, 62, 0.2)',
                position: 'relative'
              }}
            >
              {user?.role ? getInitials(user.role) : 'U'}
              <span style={{
                position: 'absolute',
                bottom: '0',
                right: '0',
                width: '12px',
                height: '12px',
                background: '#10b981',
                borderRadius: '50%',
                border: '2px solid white'
              }}></span>
            </div>
            <div style={{ textAlign: 'left' }}>
              <div
                style={{
                  color: '#1f2937',
                  fontSize: '15px',
                  fontWeight: '600',
                  marginBottom: '2px'
                }}
              >
                User #{user?.id || 'Unknown'}
              </div>
              <div
                style={{
                  color: '#6b7280',
                  fontSize: '13px',
                  textTransform: 'capitalize',
                }}
              >
                {user?.role || 'User'}
              </div>
            </div>
            <i className="pi pi-chevron-down" style={{ fontSize: '12px', color: '#9ca3af', marginLeft: '4px' }}></i>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes pulse {
          0%, 100% {
            opacity: 1;
          }
          50% {
            opacity: 0.5;
          }
        }
      `}</style>
    </>
  );
};

export default TopBar;